import React, { useState, useEffect, useContext, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import ApiURL from "../Api/Api";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { SidebarContext } from "./SidebarContext";
import TransactionImageField from "./TransactionImageField";
import { fetchTransactionPhotoRequired, imagePayload } from "../utils/transactionPhoto";
import TransactionProofImage from "./TransactionProofImage";

// ─── Icons (inline SVG to avoid extra deps) ──────────────────────────────────
const Icon = {
  Check: () => (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ),
  Close: () => (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  ),
  ArrowReturn: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
    </svg>
  ),
  Wallet: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  History: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Loader: () => (
    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
    </svg>
  ),
  Info: () => (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  ),
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending:  { bg: "bg-amber-50 border-amber-200",  text: "text-amber-700",  dot: "bg-amber-400",  label: "Pending"  },
    approved: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-400", label: "Approved" },
    rejected: { bg: "bg-rose-50 border-rose-200",    text: "text-rose-700",   dot: "bg-rose-400",   label: "Rejected" },
    returned: { bg: "bg-slate-100 border-slate-200", text: "text-slate-500",  dot: "bg-slate-400",  label: "Returned" },
    available:{ bg: "bg-sky-50 border-sky-200",      text: "text-sky-700",    dot: "bg-sky-400",    label: "Available"},
  };
  const s = map[status?.toLowerCase()] || map.available;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Ico, accent }) => (
  <div className={`relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm`}>
    <div className={`absolute inset-0 opacity-5 ${accent}`} />
    <div className="relative flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{label}</p>
        <p className="mt-1.5 text-2xl font-bold text-slate-800 tracking-tight">{value}</p>
        {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent} text-white shadow-sm`}>
        <Ico />
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminReturnRequestManagement = () => {
  const [returnRequests, setReturnRequests]     = useState([]);
  const [transactions, setTransactions]         = useState([]);
  const [netBalance, setNetBalance]             = useState(0);
  const [isLoading, setIsLoading]               = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [partialAmounts, setPartialAmounts]     = useState({});
  const [purpose, setPurpose]                   = useState("");
  const [showReturnModal, setShowReturnModal]   = useState(false);
  const [activeTab, setActiveTab]               = useState("transactions"); // "transactions" | "history"
  const [transactionImage, setTransactionImage] = useState(null);
  const [photoRequired, setPhotoRequired] = useState(false);

  const { isSidebarOpen } = useContext(SidebarContext);
  const navigate  = useNavigate();
  const location  = useLocation();
  const user_id   = localStorage.getItem("id");
  const role      = localStorage.getItem("role");
  const urlParams = new URLSearchParams(location.search);
  const highlightTransactionId = urlParams.get("highlight");

  // ── helpers ──────────────────────────────────────────────────────────────────
  const fmt = (v) => v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "₹0";

  const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return isNaN(d) ? s : d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" });
  };

  const fmtDateTime = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return isNaN(d) ? s : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  };

  // ── computed ─────────────────────────────────────────────────────────────────
  const availableCredit = useMemo(() =>
    transactions.filter(t => t.amount_type === "credit" && !t.is_returned), [transactions]);

  // FIX: compute real-time remaining balance = netBalance − sum of selected return amounts
  const selectedReturnTotal = useMemo(() =>
    selectedTransactions.reduce((s, t) => s + (parseFloat(partialAmounts[t.transaction_no]) || 0), 0),
  [selectedTransactions, partialAmounts]);

  const remainingAfterReturn = netBalance - selectedReturnTotal;

  // ── fetch transactions ────────────────────────────────────────────────────────
  const fetchTransactions = async () => {
    if (role !== "admin" || !user_id) return;
    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getMyTransactionReport.php`);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("role", "admin");

      const res  = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (data.status === "success") {
        const credits = (data.transactions || [])
          .filter(t => String(t.amount_type || "").toLowerCase() === "credit")
          .sort((a, b) => {
            const diff = new Date(a.transaction_date) - new Date(b.transaction_date);
            return diff !== 0 ? diff : new Date(a.created_at) - new Date(b.created_at);
          });

        const withSerial = credits.map((t, i) => ({ ...t, serial_no: i + 1, is_returned: false }));
        setTransactions(withSerial);

        const latest = withSerial[withSerial.length - 1];
        setNetBalance(parseFloat(latest?.net_balance || 0));

        if (highlightTransactionId) {
          const hit = withSerial.find(t =>
            String(t.transaction_no) === String(highlightTransactionId) ||
            String(t.display_transaction_no || "") === String(highlightTransactionId)
          );
          if (hit && !hit.is_returned) {
            setSelectedTransactions([hit]);
            setPartialAmounts({ [hit.transaction_no]: parseFloat(hit.credit_amount) || 0 });
          } else {
            toast.error(hit ? "Transaction already returned." : "Transaction not found.");
          }
        }
      } else {
        throw new Error(data.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error("Failed to fetch transactions: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ── fetch return requests & mark returned ─────────────────────────────────────
  const fetchReturnRequests = async () => {
    if (role !== "admin" || !user_id) return;
    try {
      const url = new URL(`${ApiURL}/getAdminReturnRequests.php`);
      url.searchParams.append("admin_id", user_id);

      const res  = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (data.status === "success") {
        // FIX: mark transactions that have pending or approved return requests as returned/disabled
        const returnedNos = new Set();
        (data.requests || []).forEach(req => {
          if (req.status === "approved" || req.status === "pending") {
            (req.transactions || []).forEach(t => returnedNos.add(parseInt(t.transaction_no)));
          }
        });

        setTransactions(prev => prev.map(t => ({
          ...t,
          is_returned: returnedNos.has(parseInt(t.transaction_no)),
        })));

        // FIX: also remove already-returned txns from selected state
        setSelectedTransactions(prev =>
          prev.filter(t => !returnedNos.has(parseInt(t.transaction_no)))
        );

        setReturnRequests(data.requests || []);
      }
    } catch (err) {
      console.error("fetchReturnRequests:", err);
    }
  };

  // ── select all ────────────────────────────────────────────────────────────────
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTransactions(availableCredit);
      const amounts = {};
      availableCredit.forEach(t => { amounts[t.transaction_no] = parseFloat(t.credit_amount) || 0; });
      setPartialAmounts(amounts);
    } else {
      setSelectedTransactions([]);
      setPartialAmounts({});
    }
  };

  // ── row select ────────────────────────────────────────────────────────────────
  const handleTransactionSelect = (txn) => {
    if (txn.is_returned) { toast.warn("This transaction has already been returned."); return; }
    if (txn.amount_type !== "credit") return;

    const already = selectedTransactions.some(t => t.transaction_no === txn.transaction_no);
    if (already) {
      setSelectedTransactions(prev => prev.filter(t => t.transaction_no !== txn.transaction_no));
      setPartialAmounts(prev => { const n = { ...prev }; delete n[txn.transaction_no]; return n; });
    } else {
      setSelectedTransactions(prev => [...prev, txn]);
      setPartialAmounts(prev => ({ ...prev, [txn.transaction_no]: parseFloat(txn.credit_amount) || 0 }));
    }
  };

  // ── partial amount ────────────────────────────────────────────────────────────
  const handlePartialAmountChange = (no, value) => {
    const amount = parseFloat(value) || 0;
    const max    = parseFloat(transactions.find(t => t.transaction_no === no)?.credit_amount) || 0;
    if (amount < 0 || amount > max) {
      toast.error(`Amount must be between ₹0 and ${fmt(max)}`);
      return;
    }
    setPartialAmounts(prev => ({ ...prev, [no]: amount }));
  };

  // ── submit ────────────────────────────────────────────────────────────────────
  const submitReturnRequest = async () => {
    if (!selectedTransactions.length) { toast.error("Select at least one transaction"); return; }
    if (!purpose.trim())               { toast.error("Purpose is required"); return; }
    if (selectedReturnTotal <= 0)      { toast.error("Total return amount must be > ₹0"); return; }
    // FIX: validate against CURRENT net balance shown in modal
    if (selectedReturnTotal > netBalance) {
      toast.error(`Cannot return more than your net balance (${fmt(netBalance)})`);
      return;
    }
    if (photoRequired && !transactionImage?.dataUrl) {
      toast.error("Proof image is required by company setting");
      return;
    }

    const payload = {
      admin_id: parseInt(user_id),
      transactions: selectedTransactions.map(t => ({
        transaction_no:  parseInt(t.transaction_no),
        amount:          parseFloat(partialAmounts[t.transaction_no] || t.credit_amount),
        purpose:         purpose.trim(),
        transaction_date: t.transaction_date,
        particuler_to:   t.particuler_to,
        site:            t.site,
        main_head:       t.main_head,
        sub_head:        t.sub_head,
      })),
      total_amount:  selectedReturnTotal,
      return_type:   selectedTransactions.length > 1 ? "multiple" : "specific",
      attachment_image: imagePayload(transactionImage),
    };

    try {
      const res = await fetch(`${ApiURL}/submitAdminReturnRequest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Server error (${res.status}): ${txt.substring(0, 200)}`);
      }
      const data = await res.json();
      if (data.status === "success") {
        toast.success("Return request submitted successfully!");
        setShowReturnModal(false);
        setSelectedTransactions([]);
        setPartialAmounts({});
        setPurpose("");
        setTransactionImage(null);
        await fetchTransactions();
        await fetchReturnRequests();
      } else {
        throw new Error(data.message || "Failed to submit");
      }
    } catch (err) {
      toast.error("Submit failed: " + err.message);
    }
  };

  useEffect(() => {
    if (role === "admin") {
      fetchTransactions().then(fetchReturnRequests);
      fetchTransactionPhotoRequired(user_id).then(setPhotoRequired).catch(() => {});
    } else {
      navigate("/");
    }
  }, []);

  // ── column def ────────────────────────────────────────────────────────────────
  const allChecked = availableCredit.length > 0 &&
    selectedTransactions.length === availableCredit.length;
  const indeterminate = selectedTransactions.length > 0 && !allChecked;

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f6fa] pt-16 font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800&display=swap');
        .arr-page * { font-family: 'Geist', 'SF Pro Display', system-ui, sans-serif; }
        .arr-page input[type=checkbox] {
          width:16px; height:16px; cursor:pointer; accent-color:#4f46e5; border-radius:4px;
        }
        .arr-scroll::-webkit-scrollbar { height:6px; width:6px; }
        .arr-scroll::-webkit-scrollbar-track { background:#f1f1f1; border-radius:99px; }
        .arr-scroll::-webkit-scrollbar-thumb { background:#c7c7d4; border-radius:99px; }
        .arr-scroll::-webkit-scrollbar-thumb:hover { background:#a0a0b8; }
        .arr-row-returned { opacity:0.5; background:#f8f8fc !important; }
        .arr-row-highlight { background:#fef9ec !important; }
        .arr-row-selected  { background:#f0f0ff !important; }
        .arr-th { white-space:nowrap; }

        /* Mobile card layout */
        @media (max-width: 767px) {
          .arr-desktop-table { display:none !important; }
          .arr-mobile-cards  { display:block !important; }
        }
        @media (min-width: 768px) {
          .arr-desktop-table { display:block !important; }
          .arr-mobile-cards  { display:none !important; }
        }
      `}</style>

      <Navbar />
      <div className="flex flex-1 relative arr-page">
        <Sidebar />
        <main
          className={`flex-1 min-h-screen transition-all duration-200 ${
            isSidebarOpen ? "sm:ml-64" : "ml-0"
          }`}
        >
          <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6 space-y-6">

            {/* ── Header ── */}
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                  Return to Company Owner
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  Select credit transactions to submit a return request for approval
                </p>
              </div>
              {isLoading && (
                <div className="flex items-center gap-2 text-indigo-600 text-sm font-medium">
                  <Icon.Loader /> Refreshing…
                </div>
              )}
            </motion.div>

            {/* ── Stat Cards ── */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-3"
            >
              <StatCard label="Net Balance"       value={fmt(netBalance)}            icon={Icon.Wallet}      accent="bg-indigo-500" />
              <StatCard label="Selected for Return" value={fmt(selectedReturnTotal)} sub={`${selectedTransactions.length} txn(s)`} icon={Icon.ArrowReturn} accent="bg-violet-500" />
              <StatCard label="Balance After Return" value={fmt(remainingAfterReturn)} icon={Icon.Wallet} accent={remainingAfterReturn < 0 ? "bg-rose-500" : "bg-emerald-500"} />
              <StatCard label="Return Requests"   value={returnRequests.length}      sub={`${returnRequests.filter(r => r.status === "pending").length} pending`} icon={Icon.History} accent="bg-amber-500" />
            </motion.div>

            {/* ── Tab Bar ── */}
            <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-slate-100 w-fit">
              {[
                { id: "transactions", label: "Transactions", icon: Icon.Wallet  },
                { id: "history",      label: "Request History", icon: Icon.History },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === tab.id
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  <tab.icon />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.id === "transactions" ? "Txns" : "History"}</span>
                </button>
              ))}
            </div>

            {/* ── Transactions Panel ── */}
            <AnimatePresence mode="wait">
              {activeTab === "transactions" && (
                <motion.div
                  key="txns"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-base font-bold text-slate-800">Credit Transactions</h2>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {availableCredit.length} available · {selectedTransactions.length} selected
                      </p>
                    </div>
                    {selectedTransactions.length > 0 && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setShowReturnModal(true)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
                      >
                        <Icon.ArrowReturn />
                        Submit Return ({fmt(selectedReturnTotal)})
                      </motion.button>
                    )}
                  </div>

                  {/* ── DESKTOP TABLE ── */}
                  <div className="arr-desktop-table arr-scroll overflow-x-auto">
                    <table className="min-w-[1050px] w-full text-[13px] text-slate-700">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="arr-th px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={allChecked}
                              ref={el => { if (el) el.indeterminate = indeterminate; }}
                              disabled={availableCredit.length === 0}
                            />
                          </th>
                          {["#", "Date", "Type", "Amount", "Return Amt", "Particulars", "Site", "Main Head", "Sub Head", "Remarks", "Proof", "Created By", "Created At", "Status"].map(h => (
                            <th key={h} className="arr-th px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {transactions.length === 0 && !isLoading ? (
                          <tr>
                            <td colSpan={14} className="text-center py-16 text-slate-400 text-sm">
                              No credit transactions found
                            </td>
                          </tr>
                        ) : transactions.map((txn, i) => {
                          const isSelected  = selectedTransactions.some(t => t.transaction_no === txn.transaction_no);
                          const isHighlight = highlightTransactionId && String(txn.transaction_no) === String(highlightTransactionId);
                          return (
                            <motion.tr
                              key={txn.transaction_no || i}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.015 }}
                              className={`transition-colors cursor-pointer ${
                                txn.is_returned ? "arr-row-returned cursor-default"
                                : isHighlight   ? "arr-row-highlight"
                                : isSelected    ? "arr-row-selected hover:bg-indigo-50"
                                : "hover:bg-slate-50/80"
                              }`}
                              onClick={() => !txn.is_returned && handleTransactionSelect(txn)}
                            >
                              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                {txn.is_returned ? (
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-slate-200 text-slate-400">
                                    <Icon.Check />
                                  </span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleTransactionSelect(txn)}
                                    disabled={txn.amount_type !== "credit"}
                                  />
                                )}
                              </td>
                              <td className="px-3 py-3 font-medium text-slate-500">{txn.serial_no}</td>
                              <td className="px-3 py-3 whitespace-nowrap">{fmtDate(txn.transaction_date)}</td>
                              <td className="px-3 py-3">
                                <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                  {txn.amount_type}
                                </span>
                              </td>
                              <td className="px-3 py-3 font-semibold text-slate-800">{fmt(txn.credit_amount)}</td>
                              <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                                {isSelected && !txn.is_returned ? (
                                  <input
                                    type="number"
                                    value={partialAmounts[txn.transaction_no] ?? ""}
                                    onChange={e => handlePartialAmountChange(txn.transaction_no, e.target.value)}
                                    className="w-28 px-2 py-1.5 text-sm border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                                    min="0"
                                    max={txn.credit_amount}
                                    step="0.01"
                                    placeholder="0"
                                  />
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-slate-600">{txn.particuler_to || "—"}</td>
                              <td className="px-3 py-3 text-slate-600">{txn.site || "—"}</td>
                              <td className="px-3 py-3 text-slate-600">{txn.main_head || "—"}</td>
                              <td className="px-3 py-3 text-slate-600">{txn.sub_head || "—"}</td>
                              <td className="px-3 py-3 text-slate-500 max-w-[120px] truncate">{txn.remarks || "—"}</td>
                              <td className="px-3 py-3"><TransactionProofImage path={txn.attachment_path} /></td>
                              <td className="px-3 py-3 text-slate-600">{txn.created_by || "—"}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-slate-500">{fmtDateTime(txn.created_at)}</td>
                              <td className="px-3 py-3">
                                <StatusBadge status={txn.is_returned ? "returned" : "available"} />
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ── MOBILE CARDS ── */}
                  <div className="arr-mobile-cards divide-y divide-slate-100">
                    {transactions.length === 0 && !isLoading && (
                      <p className="text-center py-10 text-slate-400 text-sm">No transactions found</p>
                    )}
                    {transactions.map((txn, i) => {
                      const isSelected = selectedTransactions.some(t => t.transaction_no === txn.transaction_no);
                      return (
                        <motion.div
                          key={txn.transaction_no || i}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className={`p-4 ${
                            txn.is_returned ? "bg-slate-50 opacity-60"
                            : isSelected    ? "bg-indigo-50"
                            : "bg-white"
                          }`}
                          onClick={() => !txn.is_returned && handleTransactionSelect(txn)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {txn.is_returned ? (
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-slate-400 flex-shrink-0">
                                  <Icon.Check />
                                </span>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleTransactionSelect(txn)}
                                  onClick={e => e.stopPropagation()}
                                  className="flex-shrink-0"
                                />
                              )}
                              <div>
                                <p className="text-xs text-slate-400 font-medium">#{txn.serial_no} · {fmtDate(txn.transaction_date)}</p>
                                <p className="text-base font-bold text-slate-800">{fmt(txn.credit_amount)}</p>
                              </div>
                            </div>
                            <StatusBadge status={txn.is_returned ? "returned" : "available"} />
                          </div>

                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 mb-3">
                            <div><span className="text-slate-400">Particulars:</span> {txn.particuler_to || "—"}</div>
                            <div><span className="text-slate-400">Site:</span> {txn.site || "—"}</div>
                            <div><span className="text-slate-400">Main Head:</span> {txn.main_head || "—"}</div>
                            <div><span className="text-slate-400">Sub Head:</span> {txn.sub_head || "—"}</div>
                          </div>

                          {isSelected && !txn.is_returned && (
                            <div onClick={e => e.stopPropagation()}>
                              <label className="text-xs font-semibold text-indigo-700 mb-1 block">Return Amount</label>
                              <input
                                type="number"
                                value={partialAmounts[txn.transaction_no] ?? ""}
                                onChange={e => handlePartialAmountChange(txn.transaction_no, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                                min="0"
                                max={txn.credit_amount}
                                step="0.01"
                                placeholder="Enter return amount"
                              />
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* ── Footer bar ── */}
                  {selectedTransactions.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="sticky bottom-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 bg-indigo-600 text-white"
                    >
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span><span className="opacity-70">Selected:</span> <strong>{selectedTransactions.length} txn(s)</strong></span>
                        <span><span className="opacity-70">Return Total:</span> <strong>{fmt(selectedReturnTotal)}</strong></span>
                        <span><span className="opacity-70">Balance After:</span> <strong>{fmt(remainingAfterReturn)}</strong></span>
                      </div>
                      <button
                        onClick={() => setShowReturnModal(true)}
                        className="flex items-center gap-2 px-5 py-2 bg-white text-indigo-700 text-sm font-bold rounded-xl hover:bg-indigo-50 transition-colors shadow-sm"
                      >
                        <Icon.ArrowReturn />
                        Submit Return
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* ── History Panel ── */}
              {activeTab === "history" && (
                <motion.div
                  key="hist"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
                >
                  <div className="px-5 py-4 border-b border-slate-100">
                    <h2 className="text-base font-bold text-slate-800">Return Request History</h2>
                    <p className="text-xs text-slate-400 mt-0.5">{returnRequests.length} total requests</p>
                  </div>

                  {returnRequests.length === 0 ? (
                    <p className="text-center py-16 text-slate-400 text-sm">No return requests yet</p>
                  ) : (
                    <>
                      {/* Desktop */}
                      <div className="arr-desktop-table arr-scroll overflow-x-auto">
                        <table className="min-w-[750px] w-full text-[13px] text-slate-700">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              {["Request ID", "Date", "Amount", "Purpose", "Proof", "Status", "Rejection Reason", "Created At"].map(h => (
                                <th key={h} className="arr-th px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {returnRequests.map(req => (
                              <tr key={req.id} className="hover:bg-slate-50/80 transition-colors">
                                <td className="px-4 py-3 font-semibold text-indigo-600">#{req.id}</td>
                                <td className="px-4 py-3">{fmtDate(req.created_at)}</td>
                                <td className="px-4 py-3 font-semibold text-slate-800">{fmt(req.total_return_amount)}</td>
                                <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate" title={req.purpose}>{req.purpose || "—"}</td>
                                <td className="px-4 py-3"><TransactionProofImage path={req.attachment_path || req.transactions?.[0]?.attachment_path} /></td>
                                <td className="px-4 py-3"><StatusBadge status={req.status || "pending"} /></td>
                                <td className="px-4 py-3 text-slate-500">{req.rejection_reason || "—"}</td>
                                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDateTime(req.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile */}
                      <div className="arr-mobile-cards divide-y divide-slate-100">
                        {returnRequests.map(req => (
                          <div key={req.id} className="p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-bold text-indigo-600">Request #{req.id}</span>
                              <StatusBadge status={req.status || "pending"} />
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                              <div><span className="text-slate-400">Amount:</span> <strong>{fmt(req.total_return_amount)}</strong></div>
                              <div><span className="text-slate-400">Date:</span> {fmtDate(req.created_at)}</div>
                              <div className="col-span-2"><span className="text-slate-400">Purpose:</span> {req.purpose || "—"}</div>
                              {req.rejection_reason && (
                                <div className="col-span-2 text-rose-600"><span className="text-slate-400">Rejection:</span> {req.rejection_reason}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* ── Return Modal ── */}
      <AnimatePresence>
        {showReturnModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
          >
            <motion.div
              initial={{ y: 60, opacity: 0, scale: 0.97 }}
              animate={{ y: 0,  opacity: 1, scale: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Confirm Return Request</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Review details before submitting</p>
                </div>
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                >
                  <Icon.Close />
                </button>
              </div>

              {/* Modal body */}
              <div className="flex-1 overflow-y-auto arr-scroll px-6 py-4 space-y-4">

                {/* Balance summary — FIX: always shows real-time updated balance */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Current Balance", value: fmt(netBalance),            color: "bg-slate-50 border-slate-200" },
                    { label: "Returning",        value: fmt(selectedReturnTotal),   color: "bg-rose-50 border-rose-200"  },
                    { label: "Balance After",    value: fmt(remainingAfterReturn),  color: remainingAfterReturn < 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200" },
                  ].map(c => (
                    <div key={c.label} className={`rounded-xl border p-3 ${c.color}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
                      <p className="text-base font-bold text-slate-800 mt-0.5">{c.value}</p>
                    </div>
                  ))}
                </div>

                {/* Selected transactions list */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                    Selected Transactions ({selectedTransactions.length})
                  </p>
                  <div className="space-y-2 max-h-52 overflow-y-auto arr-scroll pr-1">
                    {selectedTransactions.map(txn => (
                      <div key={txn.transaction_no} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-bold text-indigo-600">Txn #{txn.transaction_no}</span>
                          <span className="text-sm font-bold text-slate-800">{fmt(partialAmounts[txn.transaction_no] || 0)}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-500">
                          <div>Credit Amt: {fmt(txn.credit_amount)}</div>
                          <div>Date: {fmtDate(txn.transaction_date)}</div>
                          <div>Site: {txn.site || "—"}</div>
                          <div>Particulars: {txn.particuler_to || "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                    Purpose <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    value={purpose}
                    onChange={e => setPurpose(e.target.value)}
                    rows={3}
                    placeholder="Describe the reason for this return…"
                    className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none bg-white text-slate-800 placeholder-slate-300"
                  />
                </div>

                <TransactionImageField
                  value={transactionImage}
                  onChange={setTransactionImage}
                  required={photoRequired}
                  label="Return proof image"
                />

                {remainingAfterReturn < 0 && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                    <Icon.Info />
                    <span>Return amount exceeds net balance. Please reduce the return amount.</span>
                  </div>
                )}
              </div>

              {/* Modal footer */}
              <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitReturnRequest}
                  disabled={remainingAfterReturn < 0 || !purpose.trim() || selectedReturnTotal <= 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors shadow-sm"
                >
                  <Icon.ArrowReturn />
                  Submit Return to Owner
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminReturnRequestManagement;
